import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { BgPlaceholder, BubbleOverlay, CgPlaceholder, CharacterSilhouette, EmptyStage, MusicBadge, PhonePlaceholder, SfxBadge } from "./placeholder.js";
const VIEWPORT_BG = "var(--surface-sunken)";
const DIALOGUE_BG = "var(--scrim-strong)";
const STRONG_TEXT = "var(--text-primary)";
const MUTED_TEXT = "var(--text-muted)";
const STAGE_TEXT = {
    en: {
        minigame: "MINIGAME",
        htmlReady: "HTML ready",
        htmlMissing: "missing HTML",
        narrator: "NARRATOR",
        you: "YOU"
    },
    "zh-CN": {
        minigame: "小游戏",
        htmlReady: "HTML 已就绪",
        htmlMissing: "缺少 HTML",
        narrator: "旁白",
        you: "你"
    }
};
function st(key, locale) {
    return (STAGE_TEXT[locale] ?? STAGE_TEXT.en)[key];
}
/**
 * Translate the fallback speaker sentinels for display. computeStageState()
 * emits the English sentinels "NARRATOR"/"YOU" when a line has no character
 * name; those are also semantic markers consumed elsewhere, so we only
 * localize them here at the render site. Real character names pass through.
 */
function displaySpeaker(speaker, locale) {
    if (speaker === "NARRATOR")
        return st("narrator", locale);
    if (speaker === "YOU")
        return st("you", locale);
    return speaker;
}
/**
 * Per-lane sprite geometry approximating PlayerStage's CharacterSlot: a tall,
 * bottom-anchored portrait biased to its screen edge (protagonist left,
 * companion right) so two portraits read as a left/right pair. The portrait is
 * over-tall and `object-fit: contain` from the bottom; the stage frame's
 * `overflow: hidden` clips the bleed, matching the player's cropped framing
 * without porting the player's exact 179%×147% side-bleed box (which is bound
 * to the player's own viewport metrics).
 */
function characterGeometry(lane) {
    const base = {
        bottom: "0%",
        height: "88%",
        objectFit: "contain",
        objectPosition: "center bottom",
        position: "absolute"
    };
    if (lane === "left") {
        return { ...base, left: "-6%", transform: "none", width: "62%" };
    }
    if (lane === "right") {
        return { ...base, right: "-6%", left: "auto", transform: "none", width: "62%" };
    }
    return { ...base, left: "50%", transform: "translateX(-50%)", width: "66%" };
}
/**
 * 9:16 stage viewport. Pure presentation — accepts a pre-computed StageState.
 * In monitor mode this is the Studio Stage Column; in player mode this is the
 * Preview viewport (reserved for T12 — visual differences come later).
 */
export function Stage({ state, mode = "monitor", locale = "en" }) {
    const showEmpty = !state.bg &&
        state.characters.length === 0 &&
        !state.cg &&
        !state.phone &&
        !state.minigame &&
        !state.dialogue;
    return (_jsxs("div", { "data-ls-stage": mode, style: {
            aspectRatio: "9 / 16",
            background: VIEWPORT_BG,
            border: "1px solid var(--border-subtle)",
            borderRadius: "var(--r-4)",
            color: STRONG_TEXT,
            height: "100%",
            margin: "0 auto",
            maxHeight: "100%",
            overflow: "hidden",
            position: "relative",
            width: "100%"
        }, children: [_jsxs("div", { style: { inset: 0, isolation: "isolate", position: "absolute" }, children: [state.bg ? renderBg(state.bg, locale) : null, state.characters.map((character, idx) => renderCharacter(character, idx, locale)), (state.bg || state.characters.length > 0) ? _jsx(ScrimBottom, {}) : null, (state.bg || state.characters.length > 0) ? _jsx(Vignette, {}) : null] }), state.cg ? renderCg(state.cg, locale) : null, state.phone ? renderPhone(state.phone, locale) : null, state.minigame ? renderMinigame(state.minigame, mode, locale) : null, state.bubble ? _jsx(BubbleOverlay, { bubble: state.bubble }) : null, state.sfx ? (_jsx(SfxBadge, { name: state.sfx.name })) : state.music ? (_jsx(MusicBadge, { name: state.music.name, state: state.music.state, locale: locale })) : null, state.dialogue ? renderDialogue(state.dialogue, locale) : null, showEmpty ? _jsx(EmptyStage, { mode: mode, locale: locale }) : null] }));
}
function renderBg(bg, locale) {
    if (bg.hasImage && bg.imageUrl) {
        return (_jsx("div", { style: {
                backgroundImage: `url(${cssUrl(bg.imageUrl)})`,
                backgroundPosition: "center",
                backgroundRepeat: "no-repeat",
                backgroundSize: "cover",
                inset: 0,
                position: "absolute"
            } }));
    }
    return _jsx(BgPlaceholder, { name: bg.name, locale: locale });
}
function renderCharacter(character, key, locale) {
    if (character.hasImage && character.imageUrl) {
        return (_jsx("img", { alt: character.name, src: character.imageUrl, style: characterGeometry(character.lane) }, `${character.name}-${key}`));
    }
    return (_jsx(CharacterSilhouette, { lane: character.lane, look: character.look, locale: locale, name: character.name }, `${character.name}-${key}`));
}
/** Soft bottom gradient behind the dialogue box, for legibility + cinematic
 *  parity with PlayerStage's `.play-scrim-bottom`. Pure decoration. */
function ScrimBottom() {
    return (_jsx("div", { "aria-hidden": true, style: {
            background: "linear-gradient(to top, rgba(0,0,0,0.55), rgba(0,0,0,0.18) 38%, transparent 60%)",
            bottom: 0,
            height: "46%",
            left: 0,
            pointerEvents: "none",
            position: "absolute",
            right: 0,
            zIndex: 1
        } }));
}
/** Cinematic edge darkening, parity with PlayerStage's `.play-vignette`. */
function Vignette() {
    return (_jsx("div", { "aria-hidden": true, style: {
            background: "radial-gradient(120% 90% at 50% 42%, transparent 58%, rgba(0,0,0,0.42) 100%)",
            inset: 0,
            pointerEvents: "none",
            position: "absolute",
            zIndex: 1
        } }));
}
function renderCg(cg, locale) {
    if (cg.hasImage && cg.imageUrl) {
        return (_jsx("div", { style: {
                backgroundImage: `url(${cssUrl(cg.imageUrl)})`,
                backgroundPosition: "center",
                backgroundRepeat: "no-repeat",
                backgroundSize: "contain",
                inset: "8% 6%",
                position: "absolute"
            } }));
    }
    return _jsx(CgPlaceholder, { name: cg.name, locale: locale });
}
function renderPhone(phone, locale) {
    return _jsx(PhonePlaceholder, { phone: phone, locale: locale });
}
function renderMinigame(minigame, mode, locale) {
    if (mode === "player" && minigame.gameUrl) {
        return (_jsx("iframe", { "data-minigame-frame": "true", title: `${st("minigame", locale)} ${minigame.name}`, src: minigame.gameUrl, sandbox: "allow-scripts", style: {
                border: 0,
                height: "100%",
                inset: 0,
                position: "absolute",
                width: "100%"
            } }));
    }
    return (_jsxs("div", { style: {
            background: "rgba(0, 0, 0, 0.34)",
            border: "1px solid var(--border-subtle)",
            borderRadius: "var(--r-3)",
            color: STRONG_TEXT,
            left: "var(--sp-3)",
            padding: "10px 12px",
            position: "absolute",
            right: "var(--sp-3)",
            top: "var(--sp-3)"
        }, children: [_jsx("div", { style: {
                    color: MUTED_TEXT,
                    fontFamily: "var(--font-mono)",
                    fontSize: "var(--fz-1)",
                    marginBottom: "var(--sp-1)"
                }, children: st("minigame", locale) }), _jsx("div", { style: { fontSize: "var(--fz-3)", lineHeight: "var(--lh-tight)" }, children: minigame.name }), minigame.description ? (_jsx("div", { style: {
                    color: MUTED_TEXT,
                    fontSize: "var(--fz-1)",
                    lineHeight: "var(--lh-tight)",
                    marginTop: "var(--sp-1)"
                }, children: minigame.description })) : null, _jsx("div", { style: {
                    color: MUTED_TEXT,
                    fontSize: "var(--fz-1)",
                    lineHeight: "var(--lh-tight)",
                    marginTop: "var(--sp-1)"
                }, children: minigame.gameUrl ? st("htmlReady", locale) : st("htmlMissing", locale) })] }));
}
function renderDialogue(dialogue, locale) {
    const speakerStyle = {
        color: MUTED_TEXT,
        fontFamily: "var(--font-mono)",
        fontSize: "var(--fz-1)",
        fontVariantCaps: "all-small-caps",
        letterSpacing: "0.05em",
        marginBottom: "var(--sp-1)"
    };
    return (_jsxs("div", { style: {
            background: DIALOGUE_BG,
            borderRadius: "var(--r-3)",
            bottom: "var(--sp-3)",
            color: STRONG_TEXT,
            fontSize: "var(--fz-3)",
            left: "var(--sp-3)",
            lineHeight: "var(--lh-tight)",
            maxHeight: "30%",
            overflow: "hidden",
            padding: "10px 12px",
            position: "absolute",
            right: "var(--sp-3)"
        }, children: [_jsxs("div", { style: speakerStyle, children: [displaySpeaker(dialogue.speaker, locale), dialogue.look ? ` · ${dialogue.look}` : ""] }), _jsx("div", { children: dialogue.text })] }));
}
function cssUrl(value) {
    return value.replaceAll('"', '\\"');
}
